import type {
  BusAlert, BusBoardRow, BusBreakdown, BusBreakdownAction, BusBreakdownCode,
  BusDepotGroup, BusRouteDetail, BusRouteSummary,
} from './types'
import { notifyForbidden } from '../lib/notifyForbidden'

/** Same shape as settings/useAdminApi.ts's adminFetch -- these endpoints are admin-gated
 *  the same way (_require_admin_raw_data), a 403 body carries `error`. */
async function busFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) {
    const msg = (body as { error?: string }).error ?? `Request failed (${r.status})`
    if (r.status === 403) notifyForbidden(msg)
    throw new Error(msg)
  }
  return body as T
}

export async function fetchBusRoutes(depotId?: string): Promise<BusRouteSummary[]> {
  const qs = depotId ? `?depot_id=${encodeURIComponent(depotId)}` : ''
  const data = await busFetch<{ routes: BusRouteSummary[] }>(`/api/bus/routes${qs}`)
  return data.routes
}

export async function fetchBusRouteDetail(routeId: string): Promise<BusRouteDetail> {
  return busFetch<BusRouteDetail>(`/api/bus/routes/${encodeURIComponent(routeId)}`)
}

export interface BusGeotagEvent {
  kind: 'stop' | string
  ts: number
  name: string | null
  route_code?: string
  direction?: string | null
  seq?: number
  lat?: number
  lon?: number
}

/** Nagpur's own counterpart to useMbmtApi.ts's fetchMbmtGeotags -- see
 *  serving/app.py's get_bus_geotags for why this only ever carries kind: 'stop' events
 *  (no 'boundary' kind -- Nagpur has no surveyed depot-boundary polygon to cross). Only
 *  ever has rows for a vehicle whose duty resolved to a route_id that day. */
export async function fetchBusGeotags(vehicleId: string, date = ''): Promise<BusGeotagEvent[]> {
  const q = date ? `&date=${encodeURIComponent(date)}` : ''
  const data = await busFetch<{ events: BusGeotagEvent[] }>(
    `/api/bus/geotags?vehicle_id=${encodeURIComponent(vehicleId)}${q}`,
  )
  return data.events ?? []
}

interface BusAlertsOpts {
  sinceHours?: number
  kind?: string
  vehicleId?: string
  limit?: number
  // SPEC.md addendum (AC-11/AC-13): dateFrom/dateTo replace sinceHours on the
  // backend when either is given (an absolute IST-day window, not additive);
  // driverName filters via a site_duty_trip join (Nagpur-only -- see
  // serving/app.py::_bus_alerts's own docstring).
  dateFrom?: string
  dateTo?: string
  driverName?: string
}

export interface BusRouteEta {
  duration_s: number | null
  duration_in_traffic_s: number | null
  eta_ts: number | null
  next_stop_duration_s?: number | null
  destination_duration_s?: number | null
  source?: string
  error?: string | null
}

/** Live Google Directions ETA (server-side), same mbmt.google_route_eta helper
 *  useMbmtApi.ts's fetchMbmtEta already calls, through /api/bus/eta's own proxy of it
 *  -- see serving/app.py's get_bus_eta. Only meaningful for a vehicle whose duty has
 *  already resolved to exactly one route_id (nagpurRouteResolve.ts's STATUS_RESOLVED)
 *  -- callers are responsible for not fetching this otherwise. */
export async function fetchBusEta(opts: {
  originLat: number
  originLon: number
  destLat: number
  destLon: number
  waypointLat?: number
  waypointLon?: number
}): Promise<BusRouteEta> {
  const q = new URLSearchParams({
    origin_lat: String(opts.originLat),
    origin_lon: String(opts.originLon),
    dest_lat: String(opts.destLat),
    dest_lon: String(opts.destLon),
  })
  if (opts.waypointLat != null && opts.waypointLon != null) {
    q.set('waypoint_lat', String(opts.waypointLat))
    q.set('waypoint_lon', String(opts.waypointLon))
  }
  return busFetch<BusRouteEta>(`/api/bus/eta?${q}`)
}

function busAlertsQuery(spv: string, opts: BusAlertsOpts): string {
  const qs = new URLSearchParams({ spv })
  if (opts.sinceHours != null) qs.set('since_hours', String(opts.sinceHours))
  if (opts.kind) qs.set('kind', opts.kind)
  if (opts.vehicleId) qs.set('vehicle_id', opts.vehicleId)
  if (opts.limit != null) qs.set('limit', String(opts.limit))
  if (opts.dateFrom) qs.set('date_from', opts.dateFrom)
  if (opts.dateTo) qs.set('date_to', opts.dateTo)
  if (opts.driverName) qs.set('driver_name', opts.driverName)
  return qs.toString()
}

export async function fetchBusAlerts(spv: string, opts: BusAlertsOpts = {}): Promise<BusAlert[]> {
  const data = await busFetch<{ alerts: BusAlert[]; total: number }>(`/api/bus/alerts?${busAlertsQuery(spv, opts)}`)
  return data.alerts
}

/** SPEC.md addendum: backs the Nagpur Alerts panel's DriverSearchField dropdown --
 *  mirrors GET /api/reports/driver-roster's shape, different (Nagpur-only) source. */
export async function fetchBusDriverRoster(spv: string): Promise<string[]> {
  const data = await busFetch<{ drivers: string[] }>(`/api/bus/driver-roster?spv=${encodeURIComponent(spv)}`)
  return data.drivers
}

/** The true count over the full window/filters, NOT the length of a limited fetch --
 *  confirmed previously a real bug: the top-strip badge read fetchBusAlerts(...).length
 *  off a limit=200 call, so once the real 24h count passed 200 it silently froze at
 *  exactly 200 forever (confirmed live: 1,141 real alerts, badge stuck at 200).
 *  limit=0 so the backend's own COUNT(*) still runs but no alert rows are fetched or
 *  serialized just to throw them away. */
export async function fetchBusAlertCount(
  spv: string, opts: { sinceHours?: number; kind?: string; vehicleId?: string } = {},
): Promise<number> {
  const data = await busFetch<{ alerts: BusAlert[]; total: number }>(
    `/api/bus/alerts?${busAlertsQuery(spv, { ...opts, limit: 0 })}`,
  )
  return data.total
}

export async function fetchBusBreakdowns(openOnly = true): Promise<BusBreakdown[]> {
  const data = await busFetch<{ breakdowns: BusBreakdown[] }>(
    `/api/bus/breakdowns?open_only=${openOnly}`,
  )
  return data.breakdowns
}

export async function openBusBreakdown(body: {
  vehicle_id: string; category?: string; severity?: string; note?: string
  contact?: string; phone?: string; eta_min?: number | null; by?: string
}): Promise<{ breakdown_id: number; vehicle_id: string; reused: boolean }> {
  return busFetch(`/api/bus/breakdowns`, { method: 'POST', body: JSON.stringify(body) })
}

export async function actOnBusBreakdown(
  breakdownId: number,
  body: { action: string; by?: string; phone?: string; note?: string },
): Promise<{ breakdown_id: number; action: string; by: string }> {
  return busFetch(`/api/bus/breakdowns/${breakdownId}/actions`, {
    method: 'POST', body: JSON.stringify(body),
  })
}

export async function fetchBusBreakdownActions(breakdownId: number): Promise<BusBreakdownAction[]> {
  const data = await busFetch<{ actions: BusBreakdownAction[] }>(
    `/api/bus/breakdowns/${breakdownId}/actions`,
  )
  return data.actions
}

export async function fetchBusBreakdownCodes(): Promise<BusBreakdownCode[]> {
  const data = await busFetch<{ codes: BusBreakdownCode[] }>('/api/bus/breakdowns/codes')
  return data.codes
}

export async function fetchBusBoard(spv: string): Promise<BusBoardRow[]> {
  const data = await busFetch<{ rows: BusBoardRow[] }>(
    `/api/bus/board?spv=${encodeURIComponent(spv)}`,
  )
  return data.rows
}

/** Groups the flat route list by depot -- there is no /api/bus/depots endpoint, every
 *  route row already carries its depot's id/name/spv, so one fetch is enough for both
 *  the depot picker and the route list underneath it. */
export function groupByDepot(routes: BusRouteSummary[]): BusDepotGroup[] {
  const byId = new Map<string, BusDepotGroup>()
  for (const r of routes) {
    let g = byId.get(r.depot_id)
    if (!g) {
      g = { depot_id: r.depot_id, depot_name: r.depot_name, spv: r.spv, routes: [] }
      byId.set(r.depot_id, g)
    }
    g.routes.push(r)
  }
  return [...byId.values()].sort((a, b) => a.depot_name.localeCompare(b.depot_name))
}
